import type { ClinicalDocumentType } from '@/entities/clinicalDocument/types'

/** Valores aceitos pela coluna `signed_documents.document_type` e pela Edge Function `sign-document`. */
export const SIGNED_DOCUMENT_TYPES = [
  'prescription_simple',
  'prescription_controlled',
  'prescription_antimicrobial',
  'medical_certificate',
  'medical_report',
  'exam_request',
  'medical_opinion',
] as const

export type SignedDocumentType = (typeof SIGNED_DOCUMENT_TYPES)[number]

/** Obrigatoriedade de assinatura ICP-Brasil por tipo de documento assinado. */
export const SIGNATURE_REQUIREMENT = {
  prescription_simple: 'optional',
  prescription_antimicrobial: 'required',
  prescription_controlled: 'required',
  medical_certificate: 'required',
  medical_report: 'required',
  exam_request: 'optional',
  medical_opinion: 'required',
} as const satisfies Record<SignedDocumentType, 'optional' | 'required'>

export type SignatureRequirementKind = (typeof SIGNATURE_REQUIREMENT)[SignedDocumentType]

export type PrescriptionSignatureKind = Extract<
  SignedDocumentType,
  'prescription_simple' | 'prescription_controlled' | 'prescription_antimicrobial'
>

const SIGNED_SET = new Set<string>(SIGNED_DOCUMENT_TYPES)

/** Mapeia tipo clínico do Synap (`clinical_documents.type`) para categoria de assinatura padrão. */
const CLINICAL_TO_SIGNED: Record<string, SignedDocumentType> = {
  prescription: 'prescription_simple',
  certificate: 'medical_certificate',
  request: 'exam_request',
  generic: 'medical_opinion',
}

/**
 * Se `documentType` já for um tipo de assinatura, retorna-o;
 * senão tenta mapear tipo clínico; fallback `prescription_simple`.
 */
export function resolveSignedDocumentType(documentType: string): SignedDocumentType {
  const t = documentType.trim()
  if (SIGNED_SET.has(t)) return t as SignedDocumentType
  const mapped = CLINICAL_TO_SIGNED[t]
  return mapped ?? 'prescription_simple'
}

/**
 * Tipo enviado ao serviço de assinatura a partir do editor de documentos clínicos.
 * Receitas usam a subclassificação escolhida pelo médico; demais tipos seguem o mapa clínico → assinatura.
 */
export function resolveSignedDocumentTypeForClinicalEditor(
  clinicalType: ClinicalDocumentType,
  prescriptionKind: PrescriptionSignatureKind | null,
): SignedDocumentType {
  if (clinicalType === 'prescription') {
    return prescriptionKind ?? 'prescription_simple'
  }
  return resolveSignedDocumentType(clinicalType)
}
